import { useNavigate, type To } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import { Copyable } from '../../components/Copyable';
import { Ic } from '../../components/Ic';
import { statusMeta } from '../../lib/raffleStatus';
import { channelForSite } from '../../lib/scraperState';
import { dateTime, relTime } from '../../lib/time';
import { DiscordEmbed } from './DiscordEmbed';
import type { RaffleRecord } from '../../api/types';

function Dm({ k, v }: { k: string; v: string }) {
  return (
    <div className="dm">
      <div className="k">{k}</div>
      <div className="v" style={{ fontSize: 14 }}>
        {v}
      </div>
    </div>
  );
}

export function RaffleDetailModal({
  raffle,
  open,
  onExited,
  closeTo = '/raffles',
}: {
  raffle: RaffleRecord;
  open?: boolean;
  onExited?: () => void;
  /** Where closing navigates to; the modal is opened from several views. */
  closeTo?: To;
}) {
  const navigate = useNavigate();
  const status = statusMeta(raffle.status);
  const stillOpen = raffle.cutoff !== null && raffle.cutoff * 1000 > Date.now();

  return (
    <Modal
      size="lg"
      title={raffle.product ?? 'Raffle'}
      icon={Ic.tag}
      onClose={() => navigate(closeTo)}
      open={open}
      onExited={onExited}
      footer={
        <>
          {raffle.product && <Copyable text={raffle.product} label="Copy title" />}
          <span style={{ flex: 1 }} />
          {/* The design had "Re-post". payload_json has no `start` value, so a
              faithful re-send is impossible — offering it would post a subtly
              wrong embed. Opening the source is honest and actually useful. */}
          {raffle.link && (
            <a className="btn sm" href={raffle.link} target="_blank" rel="noreferrer noopener">
              Open link <Ic.external className="ico" />
            </a>
          )}
          <button className="btn ghost sm" onClick={() => navigate(closeTo)}>
            Close
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className={`pill ${status.pill} dot`}>{status.label}</span>
        {raffle.site && <span className="pill">{raffle.site}</span>}
        {raffle.scraper && <span className="pill">via {raffle.scraper}</span>}
        <span className="pill">#{channelForSite(raffle.site)}</span>
        {raffle.nsf && <span className="pill yellow">NSF</span>}
      </div>

      <div className="detail-metrics" style={{ marginBottom: 18 }}>
        <Dm k="Price" v={raffle.price ? `$${raffle.price}` : '—'} />
        <Dm k="Scraper" v={raffle.scraper ?? '—'} />
        <Dm k="Detected" v={relTime(raffle.detected_at)} />
        <Dm k={stillOpen ? 'Closes' : 'Closed'} v={raffle.cutoff ? dateTime(raffle.cutoff) : '—'} />
        <Dm k="Last seen" v={relTime(raffle.last_seen_at)} />
        <Dm
          k="Entry method"
          v={raffle.collection_methods?.join(', ') || '—'}
        />
      </div>

      <div className="sec-head">
        <h2>Discord post</h2>
        <span className="desc">as sent to #{channelForSite(raffle.site)}</span>
      </div>
      <DiscordEmbed raffle={raffle} />

      {raffle.link && (
        <div className="keyval" style={{ marginTop: 16, maxWidth: '100%', overflow: 'hidden' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {raffle.link}
          </span>
          <Copyable text={raffle.link} iconOnly className="btn ghost sm icon" />
        </div>
      )}
    </Modal>
  );
}
